import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "../lib/api/client"
import { notificationKeys } from "../lib/query-keys"

export interface Notification {
  _id: string
  company: string
  user: string
  division?: string
  matter?: string
  contact?: string
  category: string
  body: string
  priority: number
  icon: string
  source_collection: string
  source_id: string
  action_url: string
  group_key: string
  read_at: number // 0 = unread, unix timestamp when read
  archived_at: number // 0 = not archived
  created_at: number
  updated_at: number
}

/**
 * Hook to fetch the current user's unread notification count.
 */
export function useNotificationCount() {
  return useQuery({
    queryKey: notificationKeys.all,
    queryFn: async () => {
      try {
        const result = await api.notifications.count()
        return result.data?.count ?? 0
      } catch {
        return 0
      }
    },
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
  })
}

/**
 * Hook to fetch recent notifications for the dropdown.
 */
export function useNotifications(limit = 5) {
  return useQuery({
    queryKey: notificationKeys.list({ limit }),
    queryFn: async () => {
      try {
        const result = await api.notifications.search({ limit })
        return (result.data?.documents ?? []) as Notification[]
      } catch {
        return [] as Notification[]
      }
    },
    staleTime: 1000 * 30,
  })
}

/**
 * Hook to mark all notifications as read.
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      return api.notifications.markAllRead()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}
